import jsPDF from "jspdf"
import { grey } from "@mui/material/colors"
import { numberFormat } from "utils"
import dayjs from "dayjs"
import { printHeader } from "../../functions/pirntHeader"
import { printDriverSalaryTopRight } from "../DriverSalary/printDriverSalaryTopRight"
import { buildSlipData } from "./slipData"
import { drawSalaryBox } from "./printDriverSalaryV3Summary"

// Same payslip header as printDriverSalaryV3Summary (company header + top-right block + the
// four รายได้/รายการหัก/สรุปยอด/ข้อมูลสรุป boxes), but ONE driver per page, with the
// driver-trip-payment lines listed underneath as the backup detail for the ค่าเที่ยว figure.
//
// The detail rows are every wage line tied to a job order (legs and per-JO extras alike), which
// is exactly what the DriverTripPaymentV3 board shows for that driver — a claimed ค้างจ่าย line
// is included, because the งวด it was claimed into is what pays it.

const PAGE = [8.3, 11.7]
const MARGIN_X = 0.3
const PAGE_BOTTOM = 11.2
const ROW_H = 0.19
const CONTENT_SIZE = 12

// x = left edge of the column (or the right edge when align is "right"), w = clip width
const COLS = [
  { key: "No", label: "No.", x: 0.52, w: 0.22, align: "right" },
  { key: "Dte", label: "วันที่วิ่งงาน", x: 0.58, w: 0.72 },
  { key: "JobNo", label: "เลขที่งาน", x: 1.32, w: 0.85 },
  { key: "JobSNm", label: "ลูกค้า", x: 2.19, w: 1.15 },
  { key: "Cont", label: "ตู้", x: 3.36, w: 1.25 },
  { key: "ExpNm", label: "รายการ", x: 4.63, w: 0.95 },
  { key: "Route", label: "เส้นทาง", x: 5.60, w: 1.65 },
  { key: "Amnt", label: "จำนวนเงิน", x: 8.00, w: 0.80, align: "right" },
]

/** the wage lines that came from a job order, in the order the work happened */
const tripRowsOf = (items) => items
  .filter(it => it.JobOrdId != null)
  .slice()
  .sort((a, b) => {
    const ta = a.StsTm || a.AptTm || ""
    const tb = b.StsTm || b.AptTm || ""
    return String(ta).localeCompare(String(tb)) || (a.JobOrdId - b.JobOrdId) || (a.ExpId - b.ExpId)
  })

const routeOf = (it) => [it.TakePlc, it.Loc, it.RtnPlc].filter(Boolean).join(" > ")
const contOf = (it) => [it.ContNo, it.ContSizeFull].filter(Boolean).join(" ")

export const printDriverSalaryV3Detail = ({ msData, payrolls, expColumns = [] }) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "in", format: PAGE })
  doc.setLineWidth(0.01)
  doc.setFont("THSarabun")

  // shorten one cell to its column width — table rows are fixed height, so never wrap
  const clip = (text, w) => {
    let t = String(text ?? "")
    if (!t) return "-"
    if (doc.getTextWidth(t) <= w) return t
    while (t.length > 1 && doc.getTextWidth(t + "…") > w) t = t.slice(0, -1)
    return t + "…"
  }

  const drawCell = (col, value, yPos) => {
    const t = clip(value, col.w)
    if (col.align === "right") doc.text(t, col.x, yPos, { align: "right" })
    else doc.text(t, col.x, yPos)
  }

  // the table's column-header band + the rule under it; returns the first data row's baseline
  const drawTableHead = (yPos) => {
    doc.setFont("THSarabun", "bold")
    doc.setFontSize(CONTENT_SIZE)
    for (const c of COLS) drawCell(c, c.label, yPos)
    doc.setDrawColor(grey[600])
    doc.line(MARGIN_X, yPos + 0.06, 8.0, yPos + 0.06)
    doc.setDrawColor(0)
    doc.setFont("THSarabun", "normal")
    return yPos + 0.06 + ROW_H
  }

  let first = true
  for (const p of payrolls) {
    const data = buildSlipData(p, expColumns)
    const rows = tripRowsOf(p.items || [])
    // signed: a deduction tied to a job order (rare) takes away from the trip total
    const total = rows.reduce((s, it) => s + (it.InOutTyp === "I" ? -1 : 1) * +(it.Amnt || 0), 0)

    let page = 1
    let idx = 0
    const firstPageNo = doc.getNumberOfPages() + (first ? 0 : 1)
    do {
      if (!first) doc.addPage(PAGE)
      first = false

      let yPos = 0.2
      // page numbering is stamped at the foot after the loop — only then is the driver's
      // total page count known
      const yTopRight = printDriverSalaryTopRight({
        doc, dialogData: data, startY: yPos + 0.1, lineHeight: 0.21,
        contentSize: 14, page, totalPage: 0,
      })
      const yHeader = printHeader({
        jsPDFDoc: doc, companyData: msData.serverData.CompanyData, startX: MARGIN_X, startY: yPos,
      })
      yPos = Math.max(yTopRight, yHeader) + 0.21 * 1.5

      // the four summary boxes lead the first page only — the continuation pages are pure detail
      if (page === 1) {
        yPos = drawSalaryBox({ doc, startX: MARGIN_X, startY: yPos, data }) + 0.25
      }

      doc.setFont("THSarabun", "bold")
      doc.setFontSize(CONTENT_SIZE + 2)
      doc.text(page === 1 ? "รายละเอียดค่าเที่ยว" : "รายละเอียดค่าเที่ยว (ต่อ)", MARGIN_X, yPos)
      doc.setFont("THSarabun", "normal")
      yPos += ROW_H + 0.04

      yPos = drawTableHead(yPos)
      doc.setFontSize(CONTENT_SIZE)

      // leave one row of room for the total line so it never lands on a page of its own
      const roomRows = Math.max(0, Math.floor((PAGE_BOTTOM - yPos) / ROW_H) - 1)
      const pageRows = rows.slice(idx, idx + roomRows)

      for (const it of pageRows) {
        idx++
        const amnt = (it.InOutTyp === "I" ? "- " : "") + numberFormat(+(it.Amnt || 0))
        drawCell(COLS[0], String(idx), yPos)
        drawCell(COLS[1], it.StsTm ? dayjs(it.StsTm).format("DD/MM/YY") : (it.AptTm ? dayjs(it.AptTm).format("DD/MM/YY") : "-"), yPos)
        drawCell(COLS[2], it.JobNo, yPos)
        drawCell(COLS[3], it.JobSNm, yPos)
        drawCell(COLS[4], contOf(it), yPos)
        drawCell(COLS[5], it.ExpNm, yPos)
        drawCell(COLS[6], routeOf(it), yPos)
        drawCell(COLS[7], amnt, yPos)
        yPos += ROW_H
      }
      if (pageRows.length === 0 && page === 1) {
        doc.text("ไม่มีรายการค่าเที่ยวในงวดนี้", MARGIN_X, yPos)
        yPos += ROW_H
      }

      // the total prints once, on the page that carries the last row
      if (idx >= rows.length) {
        doc.setDrawColor(grey[600])
        doc.line(MARGIN_X, yPos - ROW_H + 0.06, 8.0, yPos - ROW_H + 0.06)
        doc.setDrawColor(0)
        doc.setFont("THSarabun", "bold")
        doc.text(`รวมค่าเที่ยว ${rows.length} รายการ`, COLS[3].x, yPos)
        doc.text(numberFormat(total), COLS[7].x, yPos, { align: "right" })
        doc.setFont("THSarabun", "normal")
      }
      page++
    } while (idx < rows.length)

    // foot marker, so a driver whose detail runs over is still readable once the stack is split
    const totalPage = page - 1
    if (totalPage > 1) {
      doc.setFont("THSarabun", "normal")
      doc.setFontSize(CONTENT_SIZE)
      for (let k = 0; k < totalPage; k++) {
        doc.setPage(firstPageNo + k)
        doc.text(`${data.DrvNm || ""} — หน้า ${k + 1} / ${totalPage}`, 8.0, 11.45, { align: "right" })
      }
      doc.setPage(firstPageNo + totalPage - 1)
    }
  }

  const blob = new Blob([doc.output("blob")], { type: "application/pdf" })
  window.open(URL.createObjectURL(blob), "_blank")
}
